import { rawApi as api } from '../utils/apiWithLogging';

// ============= Enums =============

export enum ReturnItemStatus {
  PENDING = 'PENDING',
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  LOST = 'LOST',
  /**
   * FB-2026-0010: ของออกจากหน้างานแล้ว รออยู่ที่ออฟฟิศบริษัทลูกค้า (บริษัท รปภ) ให้เซลล์แวะเก็บ
   * **ยังเปิด/ยังไม่กลับคลัง** — ของยังถูกกันจากการเช่าใหม่จนกว่าจะกด "รับคืนแล้ว"
   */
  AT_COMPANY_OFFICE = 'AT_COMPANY_OFFICE'
}

/**
 * สถานะที่ "ยังเปิด" — ชุดเดียวกับ backend `OPEN_RETURN_ITEM_STATUSES` (ด่าน L2/การ์ด/เลยกำหนด)
 * 🚫 ห้ามเขียนลิสต์ PENDING/SCHEDULED/IN_PROGRESS เองในคอมโพเนนต์ — ค่าใหม่จะหลุดเงียบ ๆ
 */
export const OPEN_RETURN_ITEM_STATUSES: readonly ReturnItemStatus[] = [
  ReturnItemStatus.PENDING,
  ReturnItemStatus.SCHEDULED,
  ReturnItemStatus.IN_PROGRESS,
  ReturnItemStatus.AT_COMPANY_OFFICE,
];

export const isOpenReturnItemStatus = (status?: string | null): boolean =>
  Boolean(status) && (OPEN_RETURN_ITEM_STATUSES as readonly string[]).includes(String(status));

export enum ReturnSourceType {
  REPLACEMENT = 'REPLACEMENT',
  CANCELLATION = 'CANCELLATION',
  CONTRACT_END = 'CONTRACT_END',
  MANUAL = 'MANUAL'
}

export enum ReturnItemType {
  DEVICE = 'DEVICE',
  SIM = 'SIM',
  ACCESSORY = 'ACCESSORY'
}

export enum EquipmentCondition {
  GOOD = 'GOOD',
  DAMAGED = 'DAMAGED',
  LOST = 'LOST'
}

export enum ContactMethod {
  PHONE = 'PHONE',
  LINE = 'LINE',
  VISIT = 'VISIT',
  OTHER = 'OTHER'
}

export enum ContactResult {
  CONTACTED = 'CONTACTED',
  NO_ANSWER = 'NO_ANSWER',
  WRONG_NUMBER = 'WRONG_NUMBER',
  REFUSED = 'REFUSED',
  /**
   * แถวที่ "ระบบ" เขียนให้ ไม่ใช่ผลของการติดต่อลูกค้า (ตัวแรกที่ใช้: เปิดรายการที่ปิดผิดกลับมาใหม่)
   * 🚫 ห้ามใส่ในตัวเลือกที่ผู้ใช้กดเอง — backend ปฏิเสธที่ชั้น schema (422)
   *    ใช้ `SELECTABLE_CONTACT_RESULTS` เวลาวาดปุ่มให้ผู้ใช้เลือก
   */
  SYSTEM = 'SYSTEM'
}

/** ผลการติดต่อที่ผู้ใช้เลือกเองได้ — ตัด SYSTEM ออก (ระบบเขียนเองเท่านั้น) */
export const SELECTABLE_CONTACT_RESULTS: ContactResult[] = Object.values(ContactResult)
  .filter((r): r is ContactResult => r !== ContactResult.SYSTEM);

// ============= Interfaces =============

export interface ReturnItem {
  id: number;
  return_request_id: number;
  item_type: ReturnItemType;
  item_id: number;
  expected_return_date?: string;
  actual_return_date?: string;
  pickup_location?: string;
  contact_person?: string;
  contact_phone?: string;
  /** เบอร์/ชื่อผู้ติดต่อที่ backend resolve ให้สำหรับโทรตามเก็บ — ลำดับ: กรอกมือ → เคสซ่อม(รปภหน้างาน) → รปภ/ผู้ติดต่อโครงการ */
  resolved_contact_name?: string | null;
  resolved_contact_phone?: string | null;
  resolved_contact_source?: 'manual' | 'case' | 'site' | null;
  status: ReturnItemStatus;
  condition?: EquipmentCondition;
  notes?: string;
  created_at: string;
  updated_at?: string;
  /**
   * Fix ก addendum (API_CONTRACT.md): สัญญาเจ้าของแถวนี้ — เพิ่มเป็น top-level ให้ `PUT
   * /equipment-returns/items/{id}` เท่านั้น (list/detail อ่านทาง `return_request.rental_id` เดิม)
   * ใช้เช็คว่าปิดรายการนี้แล้วสัญญา "รอการยกเลิก" ของกลับครบพอปิดใบแจ้งยกเลิกได้เลยไหม (ก-1)
   */
  rental_id?: number | null;

  // Extended fields from joins
  return_request?: {
    id: number;
    rental_id: number;
    source_type: ReturnSourceType;
    request_date: string;
    reason?: string;
    created_by_user_id: number;
    rental?: {
      id: number;
      rental_number: string;
      company_id: number;
      site_id?: number;
      company?: {
        id: number;
        name: string;
        code: string;
      };
      site?: {
        id: number;
        name: string;
        code: string;
      };
    };
    // เคสซ่อมต้นทาง — ตามจาก device_replacement_id -> maintenance_id
    // null/undefined = ตามไม่ถึงเคส (แจ้งยกเลิกสัญญา หรือเปลี่ยนเครื่องนอกหน้าเคสซ่อม)
    maintenance?: {
      id: number;
      status?: string;
      type_name?: string | null;
      summary?: string | null;
    } | null;
  };

  // Device/SIM data
  device?: {
    id: number;
    device_code: string;
    code?: string; // alias of device_code (some endpoints return code)
    serial_number: string;
    model_name?: string;
    /** สถานะจริงของเครื่องตอนนี้ (GET /items/{id} เท่านั้น) — จอ "เปิดรายการใหม่" ใช้เตือนตามของจริง */
    status?: string;
    /** ป้ายไทยของ status จาก backend (utils/enum_labels) — FE ห้ามเก็บสำเนาคำไทยเอง */
    status_label?: string;
  };

  sim?: {
    id: number;
    sim_number: string;
    phone_number?: string;
    carrier?: string;
    status?: string;
    status_label?: string;
  };

  /** FB-2026-0007: วันที่ของเหตุการณ์ต้นทาง (วันที่เปลี่ยนอุปกรณ์จริง / วันยกเลิกสัญญา / …) — list เท่านั้น */
  source_event?: ReturnSourceEvent | null;
  /** PUT /items/{id} เท่านั้น: รายการที่ย้ายในคำสั่งนี้ (ตัวเอง + ชิ้นชุดเดียวกัน เมื่อเข้า "อยู่ที่บริษัท รปภ") */
  moved_item_ids?: number[];
  /** FB-2026-0007: ผู้ดำเนินการ = ผู้ทำรายการที่ออกใบตามเก็บนี้ (list เท่านั้น) */
  created_by?: ReturnPerson | null;
  /** FB-2026-0007: เซลล์เจ้าของหน่วยงาน = companies.sales_person_id (list เท่านั้น) */
  sales_person?: ReturnPerson | null;

  // Follow-up info
  follow_up_count?: number;
  last_follow_up_date?: string;
  follow_ups?: ItemFollowUp[];  // เมื่อ BE include relation
  next_follow_up_date?: string;
}

export interface ReturnSourceEvent {
  kind: 'replacement' | 'cancellation' | 'contract_end' | 'request';
  /** ป้ายจาก backend เช่น "เปลี่ยนเมื่อ" / "ยกเลิกสัญญา" / "สิ้นสุดสัญญา" / "บันทึกเมื่อ" */
  label: string;
  /** YYYY-MM-DD */
  date: string;
}

export interface ReturnPerson {
  id: number;
  name: string;
}

export interface ReturnFilterOption extends ReturnPerson {
  count: number;
  open_count: number;
}

export interface ReturnFilterOptions {
  sales_persons: ReturnFilterOption[];
  operators: ReturnFilterOption[];
}

export interface ReturnCompanySummary {
  company_id: number | null;
  company_name: string;
  item_count: number;
  device_count: number;
  sim_count: number;
}

export interface ReturnItemUpdate {
  expected_return_date?: string;
  actual_return_date?: string;
  pickup_location?: string;
  contact_person?: string;
  contact_phone?: string;
  status?: ReturnItemStatus;
  condition?: EquipmentCondition;
  // BUG-S176-004: the backend ReturnItemUpdate field is `equipment_condition` (no alias) and the
  // completion routing reads it to decide device status (GOOD→AVAILABLE / DAMAGED→DAMAGED /
  // LOST→LOST). Must send this exact key on a COMPLETED update, or a damaged/lost device silently
  // re-enters the rentable pool.
  equipment_condition?: EquipmentCondition;
  notes?: string;
}

export interface ItemFollowUp {
  id: number;
  return_item_id: number;
  follow_up_date: string;
  action_taken: string;
  contact_method: ContactMethod;
  contact_result: ContactResult;
  next_action?: string;
  next_follow_up_date?: string;
  notes?: string;
  follow_up_by_user_id: number;
  created_at: string;

  // Extended fields
  follow_up_by_username?: string;
  follow_up_by_fullname?: string;
}

export interface ItemFollowUpCreate {
  follow_up_date: string;
  action_taken: string;
  contact_method: ContactMethod;
  contact_result: ContactResult;
  next_action?: string;
  next_follow_up_date?: string;
  notes?: string;
}

/**
 * Body ของ bulk follow-up — **FLAT** ตรงกับ backend schema `BulkFollowUpCreate(ItemFollowUpBase)`
 * (ฟิลด์ follow-up กระจายอยู่ที่ top level + `item_ids`) · endpoint ทำ
 * `ItemFollowUpCreate(**bulk_in.model_dump(exclude={"item_ids"}))` จึงต้องส่งแบบ flat
 * 🚫 ห้ามห่อใน `follow_up_data` (รูปแบบ nested เดิม) — backend ไม่มีฟิลด์นั้น → 422
 */
export interface BulkFollowUpCreate extends ItemFollowUpCreate {
  item_ids: number[];
}

export interface DashboardStats {
  total_items: number;
  pending_count: number;
  scheduled_count: number;
  in_progress_count: number;
  completed_count: number;
  cancelled_count: number;
  lost_count: number;
  /** FB-2026-0010: อยู่ที่บริษัท รปภ (ยังเปิด · รวมอยู่ใน open_count) — backend รุ่นเก่าไม่ส่ง */
  at_company_office_count?: number;
  overdue_count: number;
  completion_rate: number;
  average_return_days: number;

  /** R2U-08: PENDING + SCHEDULED + IN_PROGRESS + AT_COMPANY_OFFICE (backend รุ่นเก่าไม่ส่ง) */
  open_count?: number;
  /** R2U-08: CANCELLED + LOST — ปิดโดยไม่ได้ของกลับ */
  closed_other_count?: number;

  /**
   * ฐานของ `completion_rate` — `"piece"` = นับเป็น **ของ 1 ชิ้น** (เครื่อง/ซิม แยกกัน)
   * ตั้งแต่กติกา "1 แถว = ของ 1 ชิ้น" (9 ก.ย. 2026)
   *
   * จอใช้ค่านี้เลือก *คำนำหน่วย* ที่แสดงคู่กับเปอร์เซ็นต์ — ถ้าวันหนึ่ง backend เปลี่ยนสูตร
   * แล้วส่งค่าอื่นมา จอจะเลิกพิมพ์คำว่า "ชิ้น" เองทันที (ไม่ทิ้งหน่วยเก่าค้างบนจอ)
   * · `undefined` = backend รุ่นก่อนหน้า → ถือว่าเป็น "ชิ้น" เพราะข้อมูลหลังแยกแถวเป็นชิ้นอยู่แล้ว
   */
  completion_rate_basis?: string;

  /**
   * จำนวนแถวที่ **ไม่ถูกนับ** ในทุกตัวชี้วัดด้านบน เพราะอยู่บนสัญญาที่ถูกลบไปแล้ว
   * — แต่ตารางด้านล่างยัง *แสดง* แถวพวกนี้ (ด่าน L2 ยังกันของจากการเช่าอยู่จริง
   * ถ้าซ่อนจากตารางด้วยจะกลายเป็นทางตัน: ของถูกล็อกโดยไม่มีใบให้ปิด)
   * ใช้อธิบายส่วนต่าง "การ์ด vs จำนวนแถวในตาราง" · `undefined`/0 = ไม่มีส่วนต่าง
   */
  excluded_deleted_rental_count?: number;
  /** ในจำนวนข้างบน มีกี่แถวที่ยัง "เปิดค้าง" (= ของยังถูกกันจากการเช่าจริง) */
  excluded_deleted_rental_open_count?: number;

  // By source type
  by_source: {
    REPLACEMENT: number;
    CANCELLATION: number;
    CONTRACT_END: number;
    MANUAL: number;
  };
}

export interface GetReturnItemsParams {
  page?: number;
  limit?: number;
  status?: ReturnItemStatus;
  source_type?: ReturnSourceType;
  rental_id?: number;
  device_id?: number;
  /** backend รองรับตั้งแต่ G4-02 (แถวซิมล้วน) — หน้ารายละเอียดซิมใช้ */
  sim_id?: number;
  search?: string;
  overdue_only?: boolean;
  /** FB-2026-0010: บริษัทลูกค้าของสัญญา */
  company_id?: number;
  /** FB-2026-0007: เซลล์เจ้าของหน่วยงาน */
  sales_person_id?: number;
  /** FB-2026-0007: ผู้ดำเนินการ (ผู้ออกใบ) */
  created_by_user_id?: number;
  /** 'company' = จัดกลุ่มตามบริษัท (แท็บอยู่ที่บริษัท รปภ) */
  sort?: 'company';
}

export interface ReturnItemListResponse {
  items: ReturnItem[];
  total: number;
}

/**
 * ใบตามเก็บ 1 ใบ + ทุกรายการ — GET /equipment-returns/requests/{id} (หน้า /rentals/equipment-returns/:id · 28 ต.ค. 2026)
 * แต่ละแถวใน `items` เป็นรูปเดียวกับ GET /items/{id} (backend `_item_detail_dict` ตัวเดียว)
 */
export interface ReturnRequestDetail {
  id: number;
  rental_id: number;
  source_type: ReturnSourceType;
  request_date?: string | null;
  notes?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  created_by?: { id: number; username?: string | null; full_name?: string | null } | null;
  rental?: {
    id: number;
    rental_number: string;
    status: string;
    /** สัญญาถูกลบแล้ว — ใบยังต้องปิดได้ (ทางเก็บกวาดใบผี) แต่ลิงก์ไปหน้าสัญญาจะ 404 */
    is_deleted: boolean;
    company?: { id: number; name: string } | null;
    site?: { id: number; code?: string | null; name: string } | null;
  } | null;
  device_replacement_id?: number | null;
  cancellation_notice_id?: number | null;
  cancellation_notice?: { id: number; status: string; planned_cancellation_date?: string | null } | null;
  maintenance?: { id: number; status?: string; type_name?: string | null; summary?: string | null } | null;
  /** FB-2026-0007: วันที่ของเหตุการณ์ต้นทาง (ชุดเดียวกับ list) */
  source_event?: ReturnSourceEvent | null;
  item_count: number;
  open_item_count: number;
  items: ReturnItem[];
}

// ============= API Service =============

const equipmentReturnItemService = {
  // Return Items
  getReturnItems: async (params?: GetReturnItemsParams): Promise<ReturnItemListResponse> => {
    const response = await api.get('/equipment-returns/items', { params });
    return response.data;
  },

  getReturnRequest: async (id: number): Promise<ReturnRequestDetail> => {
    const response = await api.get(`/equipment-returns/requests/${id}`);
    return response.data;
  },

  getReturnItem: async (id: number): Promise<ReturnItem> => {
    const response = await api.get(`/equipment-returns/items/${id}`);
    return response.data;
  },

  updateReturnItem: async (id: number, data: ReturnItemUpdate): Promise<ReturnItem> => {
    const response = await api.put(`/equipment-returns/items/${id}`, data);
    return response.data;
  },

  /**
   * รับคืนอุปกรณ์ชิ้นนี้ — กดครั้งเดียวจบ (ไม่ต้องไล่ นัดคืน → กำลังเก็บ → รับคืนแล้ว)
   * เรียก complete_directly ฝั่ง backend ซึ่งเดิน side effect เดียวกับการไล่สถานะทีละขั้น 100%
   * (state machine ไม่ถูกแก้ — แค่เปิดให้หน้านี้เรียกกลไกที่มีอยู่แล้วได้)
   */
  receiveReturnItem: async (
    id: number,
    data: { equipment_condition?: 'GOOD' | 'DAMAGED' | 'LOST'; actual_return_date?: string; condition_notes?: string } = {}
  ): Promise<{ id: number; status: string; previous_status: string; changed: boolean; equipment_condition?: string | null; rental_id?: number | null }> => {
    const response = await api.post(`/equipment-returns/items/${id}/receive`, data);
    return response.data;
  },

  /** รับคืนครบทั้งใบ — สัญญาหลายชิ้นกดครั้งเดียว (ธุรกรรมเดียว) */
  receiveAllInRequest: async (
    requestId: number,
    data: { equipment_condition?: 'GOOD' | 'DAMAGED' | 'LOST'; actual_return_date?: string; condition_notes?: string } = {}
  ): Promise<{ return_request_id: number; received_count: number; rental_id?: number | null }> => {
    const response = await api.post(`/equipment-returns/requests/${requestId}/receive-all`, data);
    return response.data;
  },

  /**
   * เปิดรายการที่ปิดไปแล้ว (คืนสำเร็จ / ยกเลิก / ของหาย) กลับมาเป็นสถานะเปิด — แก้กรณีกดสถานะผิด
   *
   * เคสจริง prod: กด "ของหาย" ให้ทั้งใบ ทั้งที่ได้เครื่องคืนแล้วหายแค่ซิม → หลังจากนั้น
   * เปลี่ยนสถานะต่อไม่ได้ทุกทาง และออกใบใหม่ก็ไม่ได้ (guard ใบซ้ำ) = ใบค้างตลอดกาล
   *
   * ⚠️ **ไม่เปลี่ยนสถานะเครื่อง/ซิม** โดยตั้งใจ — ของกลับเข้าคลังเมื่อปิดใบเป็น "คืนสำเร็จ"
   * เท่านั้น (กติกา L1) · เครื่องที่ถูกกดเป็น "สูญหาย" จะยังสูญหายอยู่จนกว่าจะปิดใบรอบใหม่
   *
   * คืน `items` เป็น **รายการ** เพราะแถวรูปเก่าที่ถือทั้งเครื่องและซิมจะถูกแยกเป็น 2 รายการ
   * (กด 1 ครั้ง ได้ 2 รายการ → บันทึก "เครื่องคืนแล้ว / ซิมหาย" แยกกันได้ในรอบถัดไป)
   */
  reopenItem: async (
    id: number,
    data: { reason: string; new_status?: ReturnItemStatus }
  ): Promise<{
    id: number;
    status: ReturnItemStatus;
    previous_status: string;
    reason: string;
    actual_return_date: string | null;
    equipment_condition: EquipmentCondition | null;
    split: boolean;
    items: Array<{
      id: number;
      status: ReturnItemStatus;
      device_id: number | null;
      sim_id: number | null;
      actual_return_date: string | null;
      equipment_condition: EquipmentCondition | null;
    }>;
  }> => {
    const response = await api.post(`/equipment-returns/items/${id}/reopen`, data);
    return response.data;
  },

  /**
   * FB-2026-0010: บันทึกว่าของ "อยู่ที่บริษัท รปภ แล้ว" — ไม่ปล่อยของ/ไม่แตะสต็อก
   * `include_pair` (default true) = พาชิ้นชุดเดียวกัน (เครื่อง+ซิมจากรายการเช่าเดียวกัน) ไปด้วย
   */
  markAtCompanyOffice: async (
    id: number,
    data: { note?: string; include_pair?: boolean } = {}
  ): Promise<{ id: number; status: ReturnItemStatus; previous_status: string; moved_count: number;
    items: Array<{ id: number; status: ReturnItemStatus; device_id: number | null; sim_id: number | null }> }> => {
    const response = await api.post(`/equipment-returns/items/${id}/at-company-office`, data);
    return response.data;
  },

  /** FB-2026-0007: ตัวเลือก dropdown เซลล์/ผู้ดำเนินการ — เฉพาะคนที่มีรายการจริง */
  getFilterOptions: async (): Promise<ReturnFilterOptions> => {
    const response = await api.get('/equipment-returns/items/filter-options');
    return response.data;
  },

  /** FB-2026-0015: ตัวเลขบนแท็บสถานะ — ตัวกรองชุดเดียวกับ list ยกเว้น status/company_id */
  getStatusCounts: async (
    params: Omit<GetReturnItemsParams, 'status' | 'company_id' | 'page' | 'limit' | 'sort' | 'rental_id' | 'device_id' | 'sim_id'>
  ): Promise<{ counts: Record<string, number>; total: number }> => {
    const response = await api.get('/equipment-returns/items/status-counts', { params });
    return response.data;
  },

  /** FB-2026-0010: จำนวนของต่อบริษัท (ตัวกรองชุดเดียวกับ list ยกเว้น company_id) */
  getCompanySummary: async (
    params: Omit<GetReturnItemsParams, 'company_id' | 'page' | 'limit' | 'sort' | 'rental_id' | 'device_id' | 'sim_id'>
  ): Promise<{ companies: ReturnCompanySummary[]; total: number }> => {
    const response = await api.get('/equipment-returns/items/company-summary', { params });
    return response.data;
  },

  /** ส่งออก Excel — ตัวกรองชุดเดียวกับ list (backend `_build_filters`) */
  exportExcel: async (params: Omit<GetReturnItemsParams, 'page' | 'limit'>): Promise<Blob> => {
    const response = await api.get('/equipment-returns/items/export', { params, responseType: 'blob' });
    return response.data;
  },

  getRelatedItems: async (id: number): Promise<ReturnItem[]> => {
    const response = await api.get(`/equipment-returns/items/${id}/related`);
    return response.data;
  },

  // Follow-ups
  createFollowUp: async (itemId: number, data: ItemFollowUpCreate): Promise<ItemFollowUp> => {
    const response = await api.post(`/equipment-returns/items/${itemId}/follow-ups`, data);
    return response.data;
  },

  getFollowUps: async (itemId: number): Promise<ItemFollowUp[]> => {
    const response = await api.get(`/equipment-returns/items/${itemId}/follow-ups`);
    return response.data;
  },

  bulkCreateFollowUp: async (data: BulkFollowUpCreate): Promise<ItemFollowUp[]> => {
    const response = await api.post('/equipment-returns/items/bulk-follow-up', data);
    return response.data;
  },

  // Dashboard
  getDashboardStats: async (): Promise<DashboardStats> => {
    const response = await api.get('/equipment-returns/dashboard/stats');
    return response.data;
  },

  // Helper functions
  getStatusLabel: (status: ReturnItemStatus): string => {
    const labels: Record<ReturnItemStatus, string> = {
      [ReturnItemStatus.PENDING]: 'รอดำเนินการ',
      [ReturnItemStatus.SCHEDULED]: 'นัดวันคืนแล้ว',
      [ReturnItemStatus.IN_PROGRESS]: 'กำลังดำเนินการ',
      [ReturnItemStatus.COMPLETED]: 'คืนสำเร็จ',
      [ReturnItemStatus.CANCELLED]: 'ยกเลิก',
      [ReturnItemStatus.LOST]: 'ของหาย',
      [ReturnItemStatus.AT_COMPANY_OFFICE]: 'อยู่ที่บริษัท รปภ'
    };
    return labels[status] || status;
  },

  getSourceTypeLabel: (sourceType: ReturnSourceType): string => {
    const labels: Record<ReturnSourceType, string> = {
      [ReturnSourceType.REPLACEMENT]: 'เปลี่ยนอุปกรณ์',
      [ReturnSourceType.CANCELLATION]: 'ยกเลิกสัญญา',
      [ReturnSourceType.CONTRACT_END]: 'สิ้นสุดสัญญา',
      [ReturnSourceType.MANUAL]: 'คืนปกติ'
    };
    return labels[sourceType] || sourceType;
  },

  /**
   * @deprecated Use React Icons directly in components instead.
   * Icons: RiExchangeLine (REPLACEMENT), RiProhibitedLine (CANCELLATION),
   *        RiCalendarEventLine (CONTRACT_END), RiArchiveLine (MANUAL)
   */
  getSourceTypeIcon: (sourceType: ReturnSourceType): string => {
    // Deprecated: emoji icons replaced with React Icons in UI components
    const icons: Record<ReturnSourceType, string> = {
      [ReturnSourceType.REPLACEMENT]: 'exchange',
      [ReturnSourceType.CANCELLATION]: 'prohibited',
      [ReturnSourceType.CONTRACT_END]: 'calendar',
      [ReturnSourceType.MANUAL]: 'archive'
    };
    return icons[sourceType] || 'file';
  },

  getSourceTypeColor: (sourceType: ReturnSourceType): string => {
    const colors: Record<ReturnSourceType, string> = {
      [ReturnSourceType.REPLACEMENT]: 'blue',
      [ReturnSourceType.CANCELLATION]: 'red',
      [ReturnSourceType.CONTRACT_END]: 'orange',
      [ReturnSourceType.MANUAL]: 'gray'
    };
    return colors[sourceType] || 'gray';
  },

  getContactMethodLabel: (method: ContactMethod): string => {
    const labels: Record<ContactMethod, string> = {
      [ContactMethod.PHONE]: 'โทรศัพท์',
      [ContactMethod.LINE]: 'ไลน์',
      [ContactMethod.VISIT]: 'ไปเก็บที่หน้างาน',
      [ContactMethod.OTHER]: 'อื่นๆ'
    };
    return labels[method] || method;
  },

  getContactResultLabel: (result: ContactResult): string => {
    const labels: Record<ContactResult, string> = {
      [ContactResult.CONTACTED]: 'ติดต่อได้',
      [ContactResult.NO_ANSWER]: 'ไม่รับสาย',
      [ContactResult.WRONG_NUMBER]: 'เบอร์ผิด',
      [ContactResult.REFUSED]: 'ปฏิเสธ',
      [ContactResult.SYSTEM]: 'ระบบบันทึกให้'
    };
    return labels[result] || result;
  },

  getConditionLabel: (condition: EquipmentCondition): string => {
    const labels: Record<EquipmentCondition, string> = {
      [EquipmentCondition.GOOD]: 'สภาพดี',
      [EquipmentCondition.DAMAGED]: 'ชำรุด/เสียหาย',
      [EquipmentCondition.LOST]: 'ของหาย/สูญหาย'
    };
    return labels[condition] || condition;
  }
};

export default equipmentReturnItemService;
